import {
  type Category,
  CategoryType,
  DateSortAttribute,
  DateSortDirection,
} from "mws-ts-v2-sdk";

import { CategorySorting, CategoryTabs } from "~/types/Category";

const useCategoryFilters = (
  category: MaybeRefOrGetter<Category | undefined>,
  tab: MaybeRefOrGetter<CategoryTabs | undefined>,
) => {
  const featureToggles = useFeatureToggles();

  const isCategoryEventFiltersEnabled = computed(() => {
    if (!featureToggles.value.categoryEventFilters) return false;

    const categoryValue = toValue(category);
    const isSportOrGenderCategory =
      categoryValue?.externalReference?.type === CategoryType.Sport ||
      categoryValue?.externalReference?.type === CategoryType.Gender;

    // dont show filters due to its performance issues related to the database load
    // @todo remove this when the performance issues are resolved
    return !isSportOrGenderCategory;
  });

  const queryCategories = useDebouncedRouteQuery("category");
  const filterCategories = computed(() => {
    return transformRouteParams(queryCategories.debounced.value, "array") ?? [];
  });

  const querySortOption = useRouteQuery<CategorySorting | undefined>("sort");

  const sortBy = computed<DateSortAttribute | undefined>(() => {
    const tabValue = toValue(tab);

    // historic events will always be sorted by end date
    if (tabValue === CategoryTabs.Historical) return DateSortAttribute.EndDate;

    // active and upcoming events will be sorted by featured if not specified
    if (!querySortOption.value) return;

    if (
      querySortOption.value === CategorySorting.ClosingTimeFirstToLast ||
      querySortOption.value === CategorySorting.ClosingTimeLastToFirst
    )
      return DateSortAttribute.EndDate;

    if (
      querySortOption.value === CategorySorting.StartingTimeFirstToLast ||
      querySortOption.value === CategorySorting.StartingTimeLastToFirst
    )
      return DateSortAttribute.StartDate;

    return tabValue === CategoryTabs.Live
      ? DateSortAttribute.EndDate
      : DateSortAttribute.StartDate;
  });

  const sortDirection = computed<DateSortDirection | undefined>(() => {
    const tabValue = toValue(tab);

    // historic events should by default be sorted by latest closing time
    // active and upcoming events will be sorted by featured if not specified
    if (!querySortOption.value && tabValue !== CategoryTabs.Historical) return;

    if (
      querySortOption.value === CategorySorting.ClosingTimeFirstToLast ||
      querySortOption.value === CategorySorting.StartingTimeFirstToLast
    )
      return DateSortDirection.Ascending;

    return DateSortDirection.Descending;
  });

  const onClearSort = () => {
    querySortOption.value = undefined;
  };

  const clearFilters = () => {
    queryCategories.query.value = undefined;
  };

  return {
    enabled: isCategoryEventFiltersEnabled,
    filters: filterCategories,
    sort: {
      by: sortBy,
      direction: sortDirection,
      clear: onClearSort,
    },
    clearFilters: clearFilters,
  };
};

export default useCategoryFilters;
